import { useState } from 'react';

interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

const faqData: FAQItem[] = [
  // About BarathVector
  {
    category: 'About',
    question: 'What is BarathVector?',
    answer: 'BarathVector is India\'s progressive news platform covering politics, technology, economy, environment, education, and global strategy. We provide independent journalism focused on India\'s growth story and the voices shaping our nation\'s future.'
  },
  {
    category: 'About',
    question: 'Is BarathVector free to read?',
    answer: 'Yes! All articles and content on BarathVector are completely free to read. No subscription required.'
  },
  // Polls
  {
    category: 'Polls',
    question: 'How do I vote in polls?',
    answer: 'To vote, click on any poll and select your preferred option. You\'ll need to sign in with Google to submit your vote. Each user can vote once per poll.'
  },
  {
    category: 'Polls',
    question: 'Why do I need to sign in to vote?',
    answer: 'Sign-in ensures one vote per person, preventing duplicate votes and maintaining poll integrity. We offer Google Sign-In for quick, secure authentication.'
  },
  {
    category: 'Polls',
    question: 'Can I change my vote?',
    answer: 'No, votes are final once submitted. This ensures accurate polling results. Please consider your choice carefully before voting.'
  },
  {
    category: 'Polls',
    question: 'What poll categories are available?',
    answer: 'We cover 6 categories: Politics, Global Strategy, Environment, Technology, Economy, and Education. Filter polls by category on the Polls page.'
  },
  // Comments
  {
    category: 'Comments',
    question: 'How do I comment on articles?',
    answer: 'Sign in with Google at the bottom of any article, then type your comment and submit. Comments appear after a brief moderation check.'
  },
  {
    category: 'Comments',
    question: 'Are comments moderated?',
    answer: 'Yes, we maintain a respectful community. Comments with hate speech, spam, or inappropriate content will be removed.'
  },
  // Navigation
  {
    category: 'Navigation',
    question: 'How do I find articles by topic?',
    answer: 'Use the category filters on the homepage or browse by section: Politics, Technology, Economy, Environment, Education, and Global Affairs.'
  },
  {
    category: 'Navigation',
    question: 'How do I watch video content?',
    answer: 'Visit the Videos section from the main navigation. We feature AI-anchored debates and analysis on current affairs.'
  },
  // Account
  {
    category: 'Account',
    question: 'What data do you collect?',
    answer: 'We only store your Google profile name and email for authentication. Your votes and comments are linked to your account. We don\'t sell or share personal data.'
  },
  {
    category: 'Account',
    question: 'How do I sign out?',
    answer: 'Click on your profile picture in the header to open the menu, then click "Sign Out". Your votes and comments remain saved to your account.'
  }
];

const categories = ['All', 'About', 'Polls', 'Comments', 'Navigation', 'Account'];

export default function HelpWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const filteredFAQ = selectedCategory === 'All'
    ? faqData
    : faqData.filter(item => item.category === selectedCategory);

  return (
    <>
      {/* Floating Help Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 w-14 h-14 bg-saffron hover:bg-saffron-bright text-btn-ink rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110 z-40"
        aria-label="Help"
      >
        <i className="ri-question-line text-2xl"></i>
      </button>

      {/* Help Panel Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-band z-50 flex justify-end"
          onClick={() => setIsOpen(false)}
        >
          {/* Help Panel */}
          <div
            className="w-full max-w-md bg-paper h-full shadow-2xl overflow-hidden flex flex-col animate-slide-in-right"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-band text-band-ink p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <i className="ri-customer-service-2-line"></i>
                  BarathVector Help
                </h2>
                <button
                  onClick={() => setIsOpen(false)}
                  className="w-8 h-8 rounded-full bg-paper-2 hover:bg-paper-2 flex items-center justify-center transition-colors"
                >
                  <i className="ri-close-line text-lg"></i>
                </button>
              </div>
              <p className="text-band-ink text-sm">
                Find answers to common questions about BarathVector
              </p>
            </div>

            {/* Category Tabs */}
            <div className="p-4 border-b border-rule overflow-x-auto">
              <div className="flex gap-2 min-w-max">
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => {
                      setSelectedCategory(category);
                      setExpandedIndex(null);
                    }}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                      selectedCategory === category
                        ? 'bg-saffron text-btn-ink'
                        : 'bg-paper-2 text-ink-soft hover:bg-rule'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>

            {/* FAQ List */}
            <div className="flex-1 overflow-y-auto p-4">
              <div className="space-y-3">
                {filteredFAQ.map((item, index) => (
                  <div
                    key={index}
                    className="border border-rule rounded-lg overflow-hidden"
                  >
                    <button
                      onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
                      className="w-full p-4 text-left flex items-start justify-between gap-3 hover:bg-paper-2 transition-colors"
                    >
                      <span className="font-medium text-ink">{item.question}</span>
                      <i className={`ri-arrow-down-s-line text-ink-faint transition-transform ${
                        expandedIndex === index ? 'rotate-180' : ''
                      }`}></i>
                    </button>
                    {expandedIndex === index && (
                      <div className="px-4 pb-4 text-ink-soft text-sm leading-relaxed border-t border-rule pt-3 bg-paper-2">
                        {item.answer}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-rule bg-paper-2">
              <p className="text-sm text-ink-faint text-center">
                Still need help? Email us at{' '}
                <a href="mailto:support@barathvector.in" className="text-saffron hover:underline">
                  support@barathvector.in
                </a>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Animation styles */}
      <style>{`
        @keyframes slide-in-right {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.3s ease-out;
        }
      `}</style>
    </>
  );
}
